import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  InputAdornment,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RoommateCard from '../../components/roommate/RoommateCard';
import { roommateAPI } from '../../utils/api';
import { roommateDetailPath } from '../../utils/listingPaths';
import MapView from '../../components/common/MapView';
import axios from 'axios';
import { formatBathrooms } from '../../utils/formatters';
import { USC_DPS_POLYGON } from '../../constants/uscDpsPolygon';

const GOOGLE_MAPS_API_KEY = 'AIzaSyABl8lts6BmCwMfw-cVqa1Xy3xDxoEJhUg';

function formatRoommateListCardAddress(r) {
  if (!r) return '';
  const street = [r.address_line1, r.address_line2].filter(Boolean).join(', ');
  const stateZip = [r.state, r.zip_code].filter(Boolean).join(' ').trim();
  const cityLine = [r.city, stateZip].filter(Boolean).join(', ');
  return [street, cityLine].filter(Boolean).join(' · ');
}

function parseLifestyleFromUrl(urlParams) {
  const raw = urlParams.get('lifestyle_tags');
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function buildApiParamsFromSearch(searchString) {
  const urlParams = new URLSearchParams(searchString);
  const params = {};

  const search = urlParams.get('search');
  if (search) params.search = search;
  const pt = urlParams.get('property_type');
  if (pt) params.property_type = pt;
  const br = urlParams.get('bedrooms');
  if (br) params.bedrooms = br;
  const ba = urlParams.get('bathrooms');
  if (ba) params.bathrooms = ba;
  const tags = parseLifestyleFromUrl(urlParams);
  if (tags.length) params.lifestyle_tags = tags;

  const lat = urlParams.get('lat');
  const lng = urlParams.get('lng');
  if (lat) params.lat = lat;
  if (lng) params.lng = lng;

  const ne_lat = urlParams.get('ne_lat');
  const ne_lng = urlParams.get('ne_lng');
  const sw_lat = urlParams.get('sw_lat');
  const sw_lng = urlParams.get('sw_lng');
  if (ne_lat && ne_lng && sw_lat && sw_lng) {
    params.ne_lat = ne_lat;
    params.ne_lng = ne_lng;
    params.sw_lat = sw_lat;
    params.sw_lng = sw_lng;
  }

  return params;
}

function RoommateSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [roommates, setRoommates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [selectedLifestyle, setSelectedLifestyle] = useState([]);
  const [propertyType, setPropertyType] = useState('');
  const [selectedRoommateId, setSelectedRoommateId] = useState(null);
  const [hoveredRoommateId, setHoveredRoommateId] = useState(null);
  const [mapCenterLat, setMapCenterLat] = useState(null);
  const [mapCenterLng, setMapCenterLng] = useState(null);
  const [selectedBedrooms, setSelectedBedrooms] = useState('');
  const [selectedBathrooms, setSelectedBathrooms] = useState('');
  const [postLimitDialog, setPostLimitDialog] = useState(false);

  // 生活方式选项
  const lifestyleOptions = [
    'Non-smoker', 'Pet Friendly', 'Quiet', 'Social', 'Clean', 'Early Bird', 
    'Night Owl', 'Fitness Enthusiast', 'Vegetarian', 'Student', 'Professional'
  ];

  const handleNewRoommatePost = async () => {
    try {
      // 获取用户当前的室友帖子数量
      const response = await roommateAPI.getMyRoommatePosts();
      const userPosts = response.data;
      console.log('User posts:', userPosts);
      
      // 检查帖子数量 - 使用results数组的长度
      const postCount = userPosts.results ? userPosts.results.length : 0;
      console.log('User posts count:', postCount);
      
      if (postCount >= 2) {
        console.log('Post limit reached, showing dialog');
        setPostLimitDialog(true);
        return;
      }
      
      // 如果数量未达到限制，跳转到创建页面
      navigate('/posts/roommate/new');
    } catch (error) {
      console.error('Failed to check user posts:', error);
      // 如果检查失败，仍然允许跳转（避免阻塞用户）
      navigate('/posts/roommate/new');
    }
  };

  const handlePostLimitDialogClose = () => {
    setPostLimitDialog(false);
  };

  const handleSearchInputChange = (event) => {
    setPendingSearch(event.target.value);
  };

  const handleSearch = async () => {
    if (!pendingSearch?.trim()) return;
    setLoading(true);
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(pendingSearch.trim())}&key=${GOOGLE_MAPS_API_KEY}`;
      const resp = await axios.get(url);
      const data = resp.data;
      if (data.status === 'OK' && data.results?.length > 0) {
        const loc = data.results[0].geometry.location;
        const vp = data.results[0].geometry.viewport;
        applyUrlFilterPatch({
          search: pendingSearch.trim(),
          lat: loc.lat,
          lng: loc.lng,
          ne_lat: vp.northeast.lat,
          ne_lng: vp.northeast.lng,
          sw_lat: vp.southwest.lat,
          sw_lng: vp.southwest.lng,
        });
      } else {
        applyUrlFilterPatch({ search: pendingSearch.trim() });
      }
    } catch {
      applyUrlFilterPatch({ search: pendingSearch.trim() });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const applyUrlFilterPatch = useCallback(
    (patch) => {
      const next = new URLSearchParams(location.search);
      const setOrDelete = (key, val) => {
        if (val != null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
          if (Array.isArray(val)) next.set(key, val.join(','));
          else next.set(key, String(val));
        } else {
          next.delete(key);
        }
      };
      if ('property_type' in patch) setOrDelete('property_type', patch.property_type);
      if ('bedrooms' in patch) setOrDelete('bedrooms', patch.bedrooms);
      if ('bathrooms' in patch) setOrDelete('bathrooms', patch.bathrooms);
      if ('lifestyle_tags' in patch) setOrDelete('lifestyle_tags', patch.lifestyle_tags);
      if ('search' in patch) setOrDelete('search', patch.search);
      if ('lat' in patch) setOrDelete('lat', patch.lat);
      if ('lng' in patch) setOrDelete('lng', patch.lng);
      if ('ne_lat' in patch) setOrDelete('ne_lat', patch.ne_lat);
      if ('ne_lng' in patch) setOrDelete('ne_lng', patch.ne_lng);
      if ('sw_lat' in patch) setOrDelete('sw_lat', patch.sw_lat);
      if ('sw_lng' in patch) setOrDelete('sw_lng', patch.sw_lng);
      const qs = next.toString();
      navigate(qs ? `/roommates?${qs}` : '/roommates');
    },
    [location.search, navigate]
  );

  const fetchRoommates = useCallback(async (searchString) => {
    setLoading(true);
    setError(null);
    try {
      const res = await roommateAPI.getRoommatePosts(buildApiParamsFromSearch(searchString));
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setRoommates(data);
    } catch {
      setError('Failed to load roommate posts');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLifestyleToggle = (lifestyle) => {
    const newSelection = selectedLifestyle.includes(lifestyle)
      ? selectedLifestyle.filter((item) => item !== lifestyle)
      : [...selectedLifestyle, lifestyle];
    applyUrlFilterPatch({ lifestyle_tags: newSelection });
  };

  const handleRoommateClick = (roommateOrId) => {
    const roommate =
      typeof roommateOrId === 'object'
        ? roommateOrId
        : roommates.find((p) => p.id === roommateOrId);
    const id = roommate?.id ?? roommateOrId;
    setSelectedRoommateId(id);
    navigate(roommateDetailPath(roommate || id));
  };

  const handleBoundsChanged = useCallback(
    (bounds) => {
      if (!bounds?.ne || !bounds?.sw) return;
      const next = new URLSearchParams(location.search);
      next.set('ne_lat', String(bounds.ne.lat));
      next.set('ne_lng', String(bounds.ne.lng));
      next.set('sw_lat', String(bounds.sw.lat));
      next.set('sw_lng', String(bounds.sw.lng));
      navigate(`/roommates?${next.toString()}`);
    },
    [location.search, navigate]
  );

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const urlSearch = p.get('search') || '';
    setPendingSearch(urlSearch);
    setSearchTerm(urlSearch);
    setPropertyType(p.get('property_type') || '');
    setSelectedBedrooms(p.get('bedrooms') || '');
    setSelectedBathrooms(p.get('bathrooms') || '');
    setSelectedLifestyle(parseLifestyleFromUrl(p));
    const lat = p.get('lat');
    const lng = p.get('lng');
    setMapCenterLat(lat ? parseFloat(lat) : null);
    setMapCenterLng(lng ? parseFloat(lng) : null);
    fetchRoommates(location.search);
  }, [location.search, fetchRoommates]);

  // 移除前端过滤逻辑，直接使用API返回的数据
  const filteredRoommates = roommates;

  // 构造markers数组
  const markers = filteredRoommates
    .filter(r => r.latitude && r.longitude)
    .map(r => ({ id: String(r.id), lat: r.latitude, lng: r.longitude }));
  // 调试：打印markers和hoveredRoommateId

  useEffect(() => {
    if (filteredRoommates.length === 0) {
      setSelectedRoommateId(null);
    } else if (!selectedRoommateId || !filteredRoommates.some(r => r.id === selectedRoommateId)) {
      setSelectedRoommateId(null);
    }
  }, [filteredRoommates]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Typography>Loading roommate posts...</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">Failed to load roommate posts. Please try again.</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Find Your Perfect Roommate
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={handleNewRoommatePost}
          sx={{
            bgcolor: '#8b6f47',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(139, 111, 71, 0.3)',
            '&:hover': {
              bgcolor: '#d4b896',
              color: '#8b6f47',
              boxShadow: '0 6px 16px rgba(139, 111, 71, 0.18)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              bgcolor: '#d4b896',
              color: '#8b6f47',
            },
            transition: 'all 0.3s ease',
          }}
        >
          New Roommate Post
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Box 
        sx={{ 
          mb: 4, 
          p: 4, 
          background: '#d4b896', 
          borderRadius: 3, 
          boxShadow: '0 8px 25px rgba(139, 111, 71, 0.3)',
        }}
      >
        {/* Search Bar */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by location, zipcode, or address"
            value={pendingSearch}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchInputKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#8b6f47' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '2px solid #8b6f47',
              },
              '& input': {
                color: '#2d3748',
                fontSize: '1rem',
                '&::placeholder': {
                  color: 'rgba(45, 55, 72, 0.6)',
                },
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            sx={{ bgcolor: '#8b6f47', color: 'white', fontWeight: 600, borderRadius: 2, px: 4 }}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>

        {/* Filter Controls */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <FormControl 
              fullWidth 
              size="medium" 
              sx={{ 
                '& .MuiSelect-select': { 
                  minWidth: '200px',
                  width: '100%',
                  color: '#2d3748',
                  fontWeight: 500,
                },
                '& .MuiInputLabel-root': {
                  color: '#2d3748',
                  fontWeight: 500,
                  width: 'auto',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <InputLabel>Property Type</InputLabel>
              <Select
                value={propertyType}
                onChange={(e) => {
                  const value = e.target.value;
                  setPropertyType(value);
                  applyUrlFilterPatch({ property_type: value });
                }}
                label="Property Type"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(45, 55, 72, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(45, 55, 72, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #8b6f47',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#2d3748',
                  },
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="House">House</MenuItem>
                <MenuItem value="Townhouse">Townhouse</MenuItem>
                <MenuItem value="Condo">Condo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl 
              fullWidth 
              size="medium" 
              sx={{ 
                '& .MuiSelect-select': { 
                  minWidth: '200px',
                  width: '100%',
                  color: '#2d3748',
                  fontWeight: 500,
                },
                '& .MuiInputLabel-root': {
                  color: '#2d3748',
                  fontWeight: 500,
                  width: 'auto',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <InputLabel>Bedrooms</InputLabel>
              <Select
                value={selectedBedrooms}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedBedrooms(value);
                  applyUrlFilterPatch({ bedrooms: value });
                }}
                label="Bedrooms"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(45, 55, 72, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(45, 55, 72, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #8b6f47',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#2d3748',
                  },
                }}
              >
                <MenuItem value="">All Bedrooms</MenuItem>
                <MenuItem value="studio">Studio</MenuItem>
                <MenuItem value="1">1 Bedroom</MenuItem>
                <MenuItem value="2">2 Bedrooms</MenuItem>
                <MenuItem value="3">3 Bedrooms</MenuItem>
                <MenuItem value="4">4 Bedrooms</MenuItem>
                <MenuItem value="5">5 Bedrooms</MenuItem>
                <MenuItem value="6">6 Bedrooms</MenuItem>
                <MenuItem value="7+">7+ Bedrooms</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl 
              fullWidth 
              size="medium" 
              sx={{ 
                '& .MuiSelect-select': { 
                  minWidth: '200px',
                  width: '100%',
                  color: '#2d3748',
                  fontWeight: 500,
                },
                '& .MuiInputLabel-root': {
                  color: '#2d3748',
                  fontWeight: 500,
                  width: 'auto',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <InputLabel>Bathrooms</InputLabel>
              <Select
                value={selectedBathrooms}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedBathrooms(value);
                  applyUrlFilterPatch({ bathrooms: value });
                }}
                label="Bathrooms"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(45, 55, 72, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid rgba(45, 55, 72, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #8b6f47',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#2d3748',
                  },
                }}
              >
                <MenuItem value="">All Bathrooms</MenuItem>
                <MenuItem value="1">1 Bathroom</MenuItem>
                <MenuItem value="2+">2+ Bathrooms</MenuItem>
                <MenuItem value="3+">3+ Bathrooms</MenuItem>
                <MenuItem value="4+">4+ Bathrooms</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Lifestyle & Preferences */}
        <Box>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: '#2d3748', 
              mb: 2, 
              fontWeight: 600,
            }}
          >
            Lifestyle & Preferences
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {lifestyleOptions.map((option) => (
              <Chip
                key={option}
                label={option}
                onClick={() => handleLifestyleToggle(option)}
                sx={{
                  m: 0.5,
                  bgcolor: selectedLifestyle.includes(option) 
                    ? 'rgba(255, 255, 255, 0.9)' 
                    : 'rgba(255, 255, 255, 0.5)',
                  color: '#2d3748',
                  borderRadius: 2,
                  fontWeight: 500,
                  border: selectedLifestyle.includes(option) 
                    ? '2px solid #8b6f47'
                    : '1px solid rgba(45, 55, 72, 0.3)',
                  boxShadow: selectedLifestyle.includes(option) 
                    ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                    : 'none',
                  '&:hover': { 
                    bgcolor: selectedLifestyle.includes(option) 
                      ? 'rgba(255, 255, 255, 1)' 
                      : 'rgba(255, 255, 255, 0.7)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                  },
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Results Section */}
      <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
        {/* Left side - Roommate Listings (Single column) */}
        <Box sx={{ 
          width: '450px', 
          flexShrink: 0
        }}>
          <Stack spacing={2}>
            {filteredRoommates.map((roommate) => {
              const cardAddress = formatRoommateListCardAddress(roommate);
              return (
              <Card
                key={roommate.id}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: String(selectedRoommateId) === String(roommate.id) ? 8 : 1,
                  border: String(selectedRoommateId) === String(roommate.id) ? '2px solid #1976d2' : 'none',
                  '&:hover': { boxShadow: 6 },
                  background: hoveredRoommateId === String(roommate.id) ? '#f5f5f5' : 'white',
                }}
                onClick={() => handleRoommateClick(roommate)}
                onMouseEnter={() => {
                  setHoveredRoommateId(String(roommate.id));
                }}
                onMouseLeave={() => setHoveredRoommateId(null)}
              >
                <Box
                  sx={{
                    height: 200,
                    backgroundColor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundImage: roommate.images && roommate.images.length > 0 
                      ? `url(${roommate.images[0]})` 
                      : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <Typography color="text.secondary" sx={{ 
                    display: (roommate.images && roommate.images.length > 0) ? 'none' : 'block' 
                  }}>
                    No Image
                  </Typography>
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6">
                      {roommate.title}
                    </Typography>
                    <Chip
                      label={roommate.signed ? "Signed" : "Not Signed"}
                      size="small"
                      color={roommate.signed ? "success" : "default"}
                      sx={{
                        fontSize: '0.75rem',
                        height: '20px',
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 500, color: 'grey.900' }}>
                    Total Price: $
                    {Number.isFinite(Number(roommate.price))
                      ? Math.round(Number(roommate.price))
                      : roommate.price}
                    /month
                  </Typography>
                  <Typography variant="body2" sx={{ mb: cardAddress ? 0.5 : 2, color: 'grey.900' }}>
                    {roommate.total_bedrooms} Bedrooms • {formatBathrooms(roommate.total_bathrooms)} Bathrooms
                  </Typography>
                  {cardAddress ? (
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.45, color: 'grey.900' }}>
                      {cardAddress}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
              );
            })}
          </Stack>
        </Box>

        {/* Right side - Map (Takes remaining space) */}
        <Box sx={{ flex: 2, minWidth: 600, display: { xs: 'none', md: 'block' } }}>
          <Paper
            sx={{
              height: '800px',
              position: 'sticky',
              top: 24,
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: '100%', height: '100%' }}>
              <MapView
                markers={markers}
                selectedId={String(selectedRoommateId)}
                hoveredId={String(hoveredRoommateId)}
                onMarkerClick={(marker) => setSelectedRoommateId(marker.id)}
                onMarkerHover={(marker) => setHoveredRoommateId(marker ? marker.id : null)}
                centerLat={mapCenterLat}
                centerLng={mapCenterLng}
                onBoundsChanged={handleBoundsChanged}
                markerData={Object.fromEntries(filteredRoommates.map(r => [String(r.id), r]))}
                allowBoundsChange={true}
                uscPolygon={USC_DPS_POLYGON}
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Post Limit Dialog */}
      <Dialog
        open={postLimitDialog}
        onClose={handlePostLimitDialogClose}
        aria-labelledby="post-limit-dialog-title"
        aria-describedby="post-limit-dialog-description"
      >
        <DialogTitle id="post-limit-dialog-title">Post Limit Reached</DialogTitle>
        <DialogContent>
          <DialogContentText id="post-limit-dialog-description">
            You have reached the maximum limit of 2 roommate posts. Please delete an existing post before creating a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePostLimitDialogClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default RoommateSearch; 